import { useState, useEffect } from "react";
import { API, useAuth } from "@/App";
import axios from "axios";
import { formatCurrency } from "@/components/Layout";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { parseDateLocal } from "@/lib/utils";
import { 
  CheckCircle, 
  Circle, 
  Calendar,
  Loader2,
  CheckCheck,
  AlertCircle
} from "lucide-react";

export default function UnpaidBillsModal({ 
  isOpen, 
  onClose, 
  onBillsUpdated 
}) {
  const { user } = useAuth();
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(new Set());
  const [markingAll, setMarkingAll] = useState(false);

  const currency = user?.currency || 'GBP';

  useEffect(() => {
    if (isOpen) {
      fetchUnpaidBills();
    }
  }, [isOpen]);

  const fetchUnpaidBills = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/bills/unpaid`, {
        withCredentials: true
      });
      setBills(response.data.bills || []);
    } catch (error) {
      console.error("Error fetching unpaid bills:", error);
      // Fallback to regular bills endpoint
      try {
        const fallbackResponse = await axios.get(`${API}/bills`, {
          withCredentials: true
        });
        const allBills = fallbackResponse.data.bills || [];
        // Filter to show bills that are due and not paid
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const unpaid = allBills.filter(bill => {
          const dueDate = parseDateLocal(bill.start_date || bill.next_due_date);
          return dueDate <= today && !bill.is_paid;
        });
        setBills(unpaid);
      } catch (fallbackError) {
        console.error("Fallback also failed:", fallbackError);
        toast.error("Failed to load bills");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsPaid = async (bill) => {
    const billId = bill.id || bill.bill_id;
    const occurrenceDate = bill.due_date || bill.start_date;
    const uniqueKey = `${billId}_${occurrenceDate}`;
    
    setUpdating(prev => new Set([...prev, uniqueKey]));
    
    try {
      // Use the payments endpoint to mark as paid
      await axios.post(`${API}/payments`, {
        bill_id: billId,
        occurrence_date: occurrenceDate,
        amount_paid: bill.amount
      }, { withCredentials: true });
      
      // Update local state - mark this specific occurrence as paid
      setBills(prev => prev.map(b => {
        const bKey = `${b.id || b.bill_id}_${b.due_date || b.start_date}`;
        return bKey === uniqueKey ? { ...b, is_paid: true } : b;
      }));
      
      toast.success("Marked as paid");
      
      // Track event
      await axios.post(`${API}/events/track`, {
        event_name: 'bill_marked_paid',
        metadata: { bill_id: billId, occurrence_date: occurrenceDate, source: 'unpaid_bills_modal' }
      }, { withCredentials: true });
      
      onBillsUpdated?.();
    } catch (error) {
      console.error("Error marking bill as paid:", error);
      toast.error("Failed to mark as paid");
    } finally {
      setUpdating(prev => {
        const newSet = new Set(prev);
        newSet.delete(uniqueKey);
        return newSet;
      });
    }
  };

  const handleMarkAllAsPaid = async () => {
    setMarkingAll(true);
    
    try {
      const unpaidBills = bills.filter(bill => !bill.is_paid);
      
      for (const bill of unpaidBills) {
        const billId = bill.id || bill.bill_id;
        const occurrenceDate = bill.due_date || bill.start_date;
        
        await axios.post(`${API}/payments`, {
          bill_id: billId,
          occurrence_date: occurrenceDate,
          amount_paid: bill.amount
        }, { withCredentials: true });
      }
      
      // Update local state
      setBills(prev => prev.map(bill => ({ ...bill, is_paid: true })));
      
      toast.success(`Marked ${unpaidBills.length} bills as paid`);
      
      // Track event
      await axios.post(`${API}/events/track`, {
        event_name: 'bills_marked_all_paid',
        metadata: { count: unpaidBills.length, source: 'unpaid_bills_modal' }
      }, { withCredentials: true });
      
      onBillsUpdated?.();
    } catch (error) {
      console.error("Error marking all as paid:", error);
      toast.error("Failed to mark all as paid");
    } finally {
      setMarkingAll(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'No date';
    const date = parseDateLocal(dateStr);
    const currentYear = new Date().getFullYear();
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'short',
      year: date.getFullYear() !== currentYear ? 'numeric' : undefined
    });
  };

  const unpaidCount = bills.filter(b => !b.is_paid).length;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-slate-900 border-slate-700 text-white">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-white flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-amber-400" />
            Unpaid Bills
          </DialogTitle>
        </DialogHeader>

        <div className="py-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : bills.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
              <p className="text-slate-300">All bills are paid!</p>
              <p className="text-sm text-slate-500 mt-1">You&apos;re all caught up</p>
            </div>
          ) : (
            <>
              {/* Mark All Button */}
              {unpaidCount > 1 && (
                <div className="mb-4">
                  <Button
                    onClick={handleMarkAllAsPaid}
                    disabled={markingAll}
                    className="w-full bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 border border-emerald-500/30"
                    data-testid="mark-all-paid-btn"
                  >
                    {markingAll ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Marking all...
                      </>
                    ) : (
                      <>
                        <CheckCheck className="w-4 h-4 mr-2" />
                        Mark all as paid ({unpaidCount})
                      </>
                    )}
                  </Button>
                </div>
              )}

              {/* Bills List */}
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {bills.map((bill, index) => {
                  const billId = bill.id || bill.bill_id;
                  const occurrenceDate = bill.due_date || bill.start_date;
                  const uniqueKey = `${billId}_${occurrenceDate}_${index}`;
                  const isUpdating = updating.has(`${billId}_${occurrenceDate}`);
                  
                  return (
                    <div
                      key={uniqueKey}
                      className={`flex items-center justify-between p-3 rounded-xl transition-all ${
                        bill.is_paid 
                          ? 'bg-emerald-500/10 border border-emerald-500/20' 
                          : 'bg-slate-800/50 border border-slate-700/50 hover:bg-slate-800'
                      }`}
                      data-testid={`bill-item-${uniqueKey}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <button
                          onClick={() => !bill.is_paid && handleMarkAsPaid(bill)}
                          disabled={bill.is_paid || isUpdating}
                          className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
                            bill.is_paid 
                              ? 'bg-emerald-500 text-white cursor-default' 
                              : 'border-2 border-slate-500 hover:border-emerald-400 hover:bg-emerald-500/10'
                          }`}
                        >
                          {isUpdating ? (
                            <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                          ) : bill.is_paid ? (
                            <CheckCircle className="w-4 h-4" />
                          ) : (
                            <Circle className="w-4 h-4 text-slate-500" />
                          )}
                        </button>
                        
                        <div className="min-w-0">
                          <p className={`font-medium truncate ${bill.is_paid ? 'text-emerald-300 line-through' : 'text-white'}`}>
                            {bill.name}
                          </p>
                          <div className="flex items-center gap-2 text-xs text-slate-400">
                            <Calendar className="w-3 h-3" />
                            {formatDate(bill.start_date || bill.next_due_date || bill.due_date)}
                          </div>
                        </div>
                      </div>

                      <div className="text-right flex-shrink-0 ml-3">
                        <p className={`font-semibold ${bill.is_paid ? 'text-emerald-400' : 'text-white'}`}>
                          {formatCurrency(bill.amount, currency)}
                        </p>
                        <p className={`text-xs ${bill.is_paid ? 'text-emerald-400' : 'text-slate-500'}`}>
                          {bill.is_paid ? 'Paid' : 'Unpaid'}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Summary */}
              {bills.length > 0 && (
                <div className="mt-4 pt-4 border-t border-slate-700">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Total unpaid</span>
                    <span className="font-semibold text-white">
                      {formatCurrency(
                        bills.filter(b => !b.is_paid).reduce((sum, b) => sum + (b.amount || 0), 0),
                        currency
                      )}
                    </span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
